import { faArrowRight, faArrowsRotate, faSave } from '@fortawesome/pro-regular-svg-icons';
import { Flex, Stack } from '@mantine/core';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISetGrowthCreditRateRequest } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  CurrencyEntry,
  Icon,
  NumberEntry,
  showError,
  Text,
  toast,
} from '@feathr/components';
import { useAccount, useRole } from '@feathr/report_components';

import * as styles from './GrowthCreditsQA.css';

const DEFAULT_DONATION_CREDITS = 80;
const DEFAULT_MEDIA_CREDITS = 0.01;
const DEFAULT_LICENSE_REDUCTION = 0.01;

function parseNumericValue(value: string | number, defaultValue = 0): number {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

const noop = (): void => {};

interface IRatePairRowProps {
  description: string;
  disabled: boolean;
  leftInput: {
    label: string;
    name: string;
    type: 'currency' | 'number';
    value: number;
  };
  onChange: (value: string | number) => void;
  rightInput: {
    label: string;
    name: string;
    type: 'currency' | 'number';
    value: number;
  };
}

function RatePairRow({
  description,
  disabled,
  leftInput,
  onChange,
  rightInput,
}: Readonly<IRatePairRowProps>): JSX.Element {
  const LeftInputComponent = leftInput.type === 'currency' ? CurrencyEntry : NumberEntry;
  const RightInputComponent = rightInput.type === 'currency' ? CurrencyEntry : NumberEntry;

  return (
    <div>
      <Flex align={'flex-end'} className={styles.ratePairRow} gap={'md'}>
        <LeftInputComponent
          className={styles.fixedInput}
          disabled={true}
          label={leftInput.label}
          name={leftInput.name}
          onChange={noop}
          value={leftInput.value}
        />
        <Icon className={styles.arrowIcon} icon={faArrowRight} />
        <RightInputComponent
          disabled={disabled}
          label={rightInput.label}
          min={0}
          name={rightInput.name}
          onChange={onChange}
          value={rightInput.value}
        />
      </Flex>
      <Text.SmallRegular c={'dimmed'}>{description}</Text.SmallRegular>
    </div>
  );
}

interface IProps {
  disabled?: boolean;
}

function ConversionRatesCard({ disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { isGlobal } = useRole();

  const ratesState = useLocalObservable(() => ({
    donationCredits: DEFAULT_DONATION_CREDITS as number,
    mediaCredits: DEFAULT_MEDIA_CREDITS as number,
    licenseReduction: DEFAULT_LICENSE_REDUCTION as number,
    isLoading: true,
    isSaving: false,

    setDonationCredits(value: number): void {
      ratesState.donationCredits = value;
    },

    setMediaCredits(value: number): void {
      ratesState.mediaCredits = value;
    },

    setLicenseReduction(value: number): void {
      ratesState.licenseReduction = value;
    },

    setIsLoading(value: boolean): void {
      ratesState.isLoading = value;
    },

    setIsSaving(value: boolean): void {
      ratesState.isSaving = value;
    },

    populate(donationEarning: number, mediaRedemption: number, licenseRedemption: number): void {
      ratesState.donationCredits = donationEarning;
      ratesState.mediaCredits = mediaRedemption;
      ratesState.licenseReduction = licenseRedemption;
    },

    reset(): void {
      ratesState.donationCredits = DEFAULT_DONATION_CREDITS;
      ratesState.mediaCredits = DEFAULT_MEDIA_CREDITS;
      ratesState.licenseReduction = DEFAULT_LICENSE_REDUCTION;
    },

    get payload(): ISetGrowthCreditRateRequest {
      return {
        donation_earning: ratesState.donationCredits,
        media_redemption: ratesState.mediaCredits,
        license_redemption: ratesState.licenseReduction,
      };
    },
  }));

  useEffect(() => {
    async function fetchRates(): Promise<void> {
      try {
        const rates = await account.getGrowthCreditRate();
        if (rates) {
          ratesState.populate(
            rates.donation_earning,
            rates.media_redemption,
            rates.license_redemption,
          );
        }
      } catch {
        // If fetch fails, use defaults - this is expected if no rate exists yet
      } finally {
        ratesState.setIsLoading(false);
      }
    }

    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ratesState is a stable MobX observable
  }, [account]);

  async function handleSave(): Promise<void> {
    ratesState.setIsSaving(true);
    try {
      await account.setGrowthCreditRate(ratesState.payload);
      toast(t('Conversion rates saved successfully.'), { type: 'success' });
    } catch (error) {
      showError(error, t);
    } finally {
      ratesState.setIsSaving(false);
    }
  }

  function handleReset(): void {
    ratesState.reset();
  }

  function handleDonationCreditsChange(value: string | number): void {
    ratesState.setDonationCredits(parseNumericValue(value));
  }

  function handleMediaCreditsChange(value: string | number): void {
    ratesState.setMediaCredits(parseNumericValue(value));
  }

  function handleLicenseReductionChange(value: string | number): void {
    ratesState.setLicenseReduction(parseNumericValue(value));
  }

  const isDisabled = disabled || ratesState.isLoading || ratesState.isSaving;

  return (
    <Card disabled={isDisabled}>
      <Card.Header
        description={t(
          'Configure the conversion rates between donations, growth credits, media credits, and license bill reduction.',
        )}
        title={t('Conversion Rate Configuration')}
      />
      <Card.Content addVerticalGap={true}>
        <Stack gap={'md'}>
          <RatePairRow
            description={t(
              'For every ${{amount}} donated, the account receives {{credits}} growth credit(s).',
              { amount: 1, credits: ratesState.donationCredits },
            )}
            disabled={isDisabled}
            leftInput={{
              label: t('Donation Amount'),
              name: 'donation-amount',
              type: 'currency',
              value: 1,
            }}
            onChange={handleDonationCreditsChange}
            rightInput={{
              label: t('Growth Credits'),
              name: 'donation-credits',
              type: 'number',
              value: ratesState.donationCredits,
            }}
          />
          <RatePairRow
            description={t(
              '{{inputCredits}} growth credit(s) can be redeemed for ${{outputCredits}} media credit(s).',
              { inputCredits: 1, outputCredits: ratesState.mediaCredits },
            )}
            disabled={isDisabled}
            leftInput={{
              label: t('Growth Credits'),
              name: 'credits-for-media',
              type: 'number',
              value: 1,
            }}
            onChange={handleMediaCreditsChange}
            rightInput={{
              label: t('Media Credits'),
              name: 'media-credits',
              type: 'currency',
              value: ratesState.mediaCredits,
            }}
          />
          <RatePairRow
            description={t(
              '{{credits}} growth credit(s) can reduce the license bill by ${{amount}}.',
              { credits: 1, amount: ratesState.licenseReduction },
            )}
            disabled={isDisabled}
            leftInput={{
              label: t('Growth Credits'),
              name: 'credits-for-license',
              type: 'number',
              value: 1,
            }}
            onChange={handleLicenseReductionChange}
            rightInput={{
              label: t('License Bill Reduction'),
              name: 'license-reduction',
              type: 'currency',
              value: ratesState.licenseReduction,
            }}
          />
        </Stack>
      </Card.Content>
      <Card.Actions>
        <Button
          disabled={isDisabled}
          onClick={handleReset}
          prefix={<Icon icon={faArrowsRotate} />}
          type={'secondary'}
        >
          {t('Reset')}
        </Button>
        <Button
          disabled={!isGlobal || isDisabled}
          onClick={handleSave}
          prefix={<Icon icon={faSave} />}
          tooltip={!isGlobal ? t('Global role required') : undefined}
          type={'primary'}
        >
          {t('Save All Rates')}
        </Button>
      </Card.Actions>
    </Card>
  );
}

export default observer(ConversionRatesCard);
